import { useState, useEffect } from "react";
import axios from "../../../../api/apiClient";
import { token, domain } from '../../../Security';
import { DetailsTab } from "../tabs/DetailsTab";
import { EmployeesTab } from '../tabs/EmployeesTab';
import CloseIcon from "@mui/icons-material/Close";
import { ConfirmationModal } from "../../../../common/Modal";

export function EmployeeHierarchyForm({ isOpen, modalName, onClose, form, setForm, handleSubmit, mode = "add", showGeneralMessage }) {
  const [activeTab, setActiveTab] = useState("details");
  const [employeesList, setEmployeesList] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (isOpen && activeTab === "employees") fetchEmployees();
  }, [isOpen, activeTab]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await axios.get(`${domain}/api/Employee`, { headers: { 'Authorization': `Bearer ${token}` } });
      setEmployeesList(response.data);
    } catch (error) {
      showGeneralMessage("Failed to load employees", false);
    } finally { setLoadingEmployees(false); }
  };

  const handleInputChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSelectMultipleSubordinates = (selectedEmployees) => {
    setForm(prev => {
      const newBatch = selectedEmployees
        .filter(emp => !prev.employees.some(existing => existing.employeePersonalInformationId === emp.id))
        .map(emp => ({
          employeePersonalInformationId: emp.id,
          employeeId: emp.employeeId,
          fullName: emp.fullName,
          department: emp.employeeInformation?.departmentDescription || emp.employeeInformationDepartmentDescription || emp.department || "-",
          position: emp.employeeInformation?.positionDescription || emp.employeeInformationPositionDescription || emp.position || "-",
        }));
      return { ...prev, employees: [...prev.employees, ...newBatch] };
    });
  };

  const handleRemoveEmployee = (id) => setForm(prev => ({ ...prev, employees: prev.employees.filter(e => e.employeePersonalInformationId !== id) }));

  const handlePreSubmit = (e) => { if (e && e.preventDefault) e.preventDefault(); setShowConfirmModal(true); };
  const handleFinalSubmit = () => { setShowConfirmModal(false); handleSubmit(); };
  const handleClose = () => { setShowConfirmModal(false); setActiveTab("details"); onClose(); };

  if (!isOpen) return null;

  const displayName = form.teamName ? `Team: ${form.teamName}` : `Hierarchy for ${form.employeeData?.fullName || "this manager"}`;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-3">
      <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[80%] lg:w-[70%]">
        <div className="flex items-center justify-between rounded-t-lg border-b bg-white px-6 py-3">
          <h2 className="text-xl text-gray-600">{modalName}</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-700 border-none bg-transparent cursor-pointer"><CloseIcon /></button>
        </div>
        <div className="border-b bg-white pt-3">
          <div className="flex space-x-1 px-6">
            <button onClick={() => setActiveTab("details")} className={`px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 ${activeTab === "details" ? "border-blue-500 text-blue-600 bg-blue-50" : "border-transparent text-gray-500"}`}>Details</button>
            <button onClick={() => setActiveTab("employees")} className={`px-4 py-3 text-sm font-medium rounded-t-lg border-b-2 ${activeTab === "employees" ? "border-blue-500 text-blue-600 bg-blue-50" : "border-transparent text-gray-500"}`}>Employees ({form.employees.length})</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "details" && <DetailsTab form={form} handleInputChange={handleInputChange} showGeneralMessage={showGeneralMessage} />}
          {activeTab === "employees" && <EmployeesTab form={form} employeesList={employeesList} loadingEmployees={loadingEmployees} handleRemoveEmployee={handleRemoveEmployee} handleSelectMultiple={handleSelectMultipleSubordinates} />}
        </div>
        <div className="flex justify-end space-x-2 rounded-b-lg border-t bg-white p-5 shadow-sm">
          <button onClick={handleClose} className="rounded bg-gray-400 px-4 py-2 text-white border-none cursor-pointer hover:bg-gray-500">Cancel</button>
          <button onClick={handlePreSubmit} disabled={!form.employeePersonalInformationId} className="rounded bg-blue-500 px-4 py-2 text-white border-none cursor-pointer disabled:bg-gray-300 hover:bg-blue-600 transition-colors">{mode === "edit" ? "Update" : "Save"}</button>
        </div>
      </div>
      <ConfirmationModal visible={showConfirmModal} onClose={() => setShowConfirmModal(false)} onConfirm={handleFinalSubmit} title={mode === "edit" ? "Confirm Update" : "Confirm Save"} message={`Are you sure you want to ${mode === "edit" ? "update" : "save"} ${displayName}?`} confirmText="Confirm" cancelText="Cancel" type="info" />
    </div>
  );
}